import type { LayoutProps } from "../types";

export default function EditorialMinimalLayout({ theme, template, articles, language }: LayoutProps) {
  const t = theme;
  const [lead, ...rest] = articles;
  const archiveLabel = language === "en" ? "Archive" : "Archives";

  return (
    <div style={{ background: t.palette.bg, color: t.palette.ink, fontFamily: t.typography.body }}>
      {/* Stark masthead */}
      <header className="mx-auto max-w-3xl px-6 pt-20 pb-12">
        <div className="flex items-baseline justify-between">
          <span style={{ fontFamily: t.typography.meta, color: t.palette.muted, letterSpacing: "0.32em" }} className="text-[10px] uppercase">
            {lead?.date}
          </span>
          <span style={{ fontFamily: t.typography.meta, color: t.palette.muted, letterSpacing: "0.32em" }} className="text-[10px] uppercase">
            N&deg; CCXII
          </span>
        </div>
        <h1
          style={{
            fontFamily: t.typography.masthead,
            color: t.palette.ink,
            letterSpacing: t.ornaments.letterspacing === "wide" ? "0.08em" : "-0.02em",
          }}
          className="mt-6 text-center text-5xl font-bold leading-none md:text-7xl"
        >
          {template.name}
        </h1>
        <div style={{ background: t.palette.accent }} className="mx-auto mt-8 h-px w-12" />
      </header>

      {/* Lead article - typography only, no hero image */}
      {lead && (
        <article className="mx-auto max-w-3xl px-6 pb-16" style={{ borderBottom: `1px solid ${t.palette.rule}` }}>
          <span style={{ fontFamily: t.typography.meta, color: t.palette.accent, letterSpacing: "0.32em" }} className="text-center text-[10px] uppercase">
            <span className="block">{lead.kicker}</span>
          </span>
          <h2
            style={{ fontFamily: t.typography.headline, color: t.palette.ink, letterSpacing: "-0.025em" }}
            className="mt-4 text-center text-4xl font-bold leading-[1.1] md:text-6xl"
          >
            {lead.title}
          </h2>
          <p style={{ fontFamily: t.typography.body, color: t.palette.muted, fontStyle: "italic" }} className="mx-auto mt-6 max-w-2xl text-center text-lg leading-relaxed md:text-xl">
            {lead.dek}
          </p>
          <p style={{ fontFamily: t.typography.meta, color: t.palette.muted, letterSpacing: "0.18em" }} className="mt-6 text-center text-[11px] uppercase">
            Par {lead.byline}
          </p>
          <div className="mx-auto mt-10 max-w-2xl space-y-6">
            {lead.body.map((p, i) => (
              <p key={i} style={{ fontFamily: t.typography.body, color: t.palette.ink }} className="text-base leading-8">
                {i === 0 && (
                  <span style={{ fontFamily: t.typography.headline, fontSize: "4rem", float: "left", lineHeight: "0.85", paddingRight: "0.5rem", paddingTop: "0.25rem", color: t.palette.ink }}>
                    {p.charAt(0)}
                  </span>
                )}
                {i === 0 ? p.slice(1) : p}
              </p>
            ))}
          </div>
        </article>
      )}

      {/* Archive list - pure typographic stack */}
      <section className="mx-auto max-w-3xl px-6 py-16">
        <h3 style={{ fontFamily: t.typography.meta, color: t.palette.muted, letterSpacing: "0.32em" }} className="mb-8 text-center text-[10px] uppercase">
          &mdash; {archiveLabel} &mdash;
        </h3>
        <ul>
          {rest.map((a) => (
            <li key={a.id} className="grid grid-cols-12 items-baseline gap-4 py-5" style={{ borderTop: `1px solid ${t.palette.rule}` }}>
              <span style={{ fontFamily: t.typography.meta, color: t.palette.muted, letterSpacing: "0.2em" }} className="col-span-2 text-[10px] uppercase">
                {a.kicker}
              </span>
              <h4 style={{ fontFamily: t.typography.headline, color: t.palette.ink, letterSpacing: "-0.01em" }} className="col-span-7 text-xl font-medium leading-snug">
                {a.title}
              </h4>
              <span style={{ fontFamily: t.typography.meta, color: t.palette.muted, letterSpacing: "0.18em" }} className="col-span-3 text-right text-[10px] uppercase">
                {a.byline}
              </span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
